import api from './api';
import type { PaginatedResponse } from '@/types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UnifiedChannelRow {
  entity_kind: 'posp' | 'channel';
  id: string;
  channel_id: string | null;
  /** The RAW Channel.TYPE_CHOICES value ('POSP', 'ADVERTISER', …). Behaviour
   *  only: advertiser nesting, the type filter and channelDetailView's POSP
   *  mode all compare it against literals. Never render it as the label when a
   *  custom type is attached -- use `type_display`. */
  type_label: string;
  /** Board #252: what a human should read. A custom channel type's own name
   *  when the channel carries one, otherwise the built-in type. */
  type_display: string;
  name: string;
  code: string;
  email: string | null;
  phone: string | null;
  status: string;
  policy_count: number;
  commission_earned: string;
  /** This row's own commission %: POSP payout % or advertiser cut %. */
  commission_percentage: string | null;
  /** Advertiser this row nests under (set on advertiser-linked POSP rows and
   *  on any channel with a parent). */
  parent_channel_id: string | null;
  parent_channel_name: string | null;
  detail_path: string;
  /** POSP-1. Present on `posp` rows only, and only for the broker that owns
   *  the agent — the server omits these keys entirely for a country admin
   *  (board #180(c)), so `undefined` here means "not shown to you", which is a
   *  different thing from `''` ("no account on file"). Read-only on this
   *  endpoint: the agent maintains them from their own portal. */
  bank_name?: string;
  bank_ifsc_code?: string;
  /** Last four digits behind bullets. The full number is never on this
   *  endpoint, for anyone. */
  bank_account_number_masked?: string;
  /** true = an agent with a real portal login (every `posp` row); false = a
   *  POSP-type channel saved WITHOUT a login (its email/phone matched an
   *  existing login, or it predates logins -- see B7); null = a non-POSP
   *  channel type, for which having a login is not applicable at all. */
  has_login?: boolean | null;
  /** M-05. `posp` rows, owning broker only: whether the agent's login has a
   *  usable password. `has_login` is true for every agent row; an agent
   *  onboarded without a password still cannot sign in until one is issued. */
  password_set?: boolean;
}

/** One row under a type group. Enough to list and open a channel, no more. */
export interface ChannelTypeGroupChannel {
  id: string;
  channel_id: string;
  name: string;
  code: string;
  channel_type: string;
  is_active: boolean;
  posp_id: string | null;
  detail_path: string;
}

/** A type and the channels under it.
 *
 *  `id` is null for a BUILT-IN group: those are enum values, not rows, so they
 *  cannot be renamed, deleted, or given a payout grid. The page keys off that
 *  rather than off the name. */
export interface ChannelTypeGroup {
  id: string | null;
  name: string;
  code: string;
  base_type: string;
  base_type_display: string;
  is_active: boolean;
  rate_count?: number;
  channels: ChannelTypeGroupChannel[];
}

export interface ChannelTypeGroups {
  /** The broker's own types. Present even when they hold no channels. */
  custom: ChannelTypeGroup[];
  /** Built-in types, carrying the channels that have no custom type. Only the
   *  non-empty ones: all seven every time buries the ones that matter. */
  built_in: ChannelTypeGroup[];
}

export interface CreateSubchannel {
  name: string;
  code?: string;
  email?: string;
  phone?: string;
  commission_percentage?: string;
}

export interface CreateChannelPayload {
  name: string;
  code?: string;
  /** Optional ONLY when `type_ref` is a custom channel type: the server derives
   *  channel_type from that type's `base_type` (board #252). Sending neither is
   *  a 400. Never guess a base type client-side. */
  channel_type?: string;
  email?: string;
  phone?: string;
  /** Login password for a POSP channel's agent. Optional — the server
   *  defaults to "Test@1234" and forces a change on first login
   *  (must_change_password) when omitted. Ignored when `posp` links an
   *  existing agent: that agent already has a login. */
  password?: string;
  commission_percentage?: string;
  parent?: string;
  type_ref?: string;
  /** Board #267: link an EXISTING agent instead of provisioning a new one.
   *  POSP-type channels only. When set the server creates no User and no POSP
   *  and leaves the agent's login untouched — only the channel row and the
   *  agent's catch-all payout rate are written. */
  posp?: string;
  subchannels?: CreateSubchannel[];
}

/** Editable fields on an existing channel.
 *
 *  `channel_type` is deliberately absent: it stays read-only server-side, since
 *  Channel.save() refuses a posp link on a non-POSP type, so retyping a
 *  POSP-backed channel is a hard error.
 *
 *  Board #267: `posp` IS writable now (it used to be forced read-only on
 *  PUT/PATCH, which is why the Linked Agent select silently dropped). The server
 *  guards it: POSP-type channels only, the agent must belong to the caller's
 *  broker, must not already be linked elsewhere, and a channel that already
 *  represents an agent cannot be pointed at a different one. `null` unlinks --
 *  the agent then gets its own owning channel back.
 */
export interface UpdateChannelPayload {
  name?: string;
  code?: string;
  email?: string;
  phone?: string;
  /** null clears the channel's default share. */
  commission_percentage?: string | null;
  parent?: string | null;
  /** Board #267: link an existing agent, or null to unlink. */
  posp?: string | null;
  is_active?: boolean;
}

/** The CHANNEL id to PATCH for a unified row — never `row.id`.
 *
 *  The id trap: on an `entity_kind === 'posp'` row, `id` is the POSP id; the
 *  channel id is in `channel_id` (for `entity_kind === 'channel'` rows the two
 *  are the same value). Falling back to `row.id` for a POSP would PATCH
 *  /broker-channels/<posp-id>/ — a different record entirely. A POSP with no
 *  channel row returns null and simply cannot be edited from the channels list.
 */
export function channelIdOf(row: UnifiedChannelRow): string | null {
  return row.channel_id;
}

export interface ChannelType {
  id: string;
  name: string;
  code: string;
  /** Board #252: the built-in Channel.TYPE_CHOICES member this custom type
   *  BEHAVES AS. Channels created from this type are stored with this
   *  `channel_type`, which is what earns commission and lands in reports. */
  base_type: string;
  base_type_display: string;
  description: string;
  is_active: boolean;
  channel_count: number;
  rate_count: number;
}

export interface CreateChannelTypePayload {
  name: string;
  code: string;
  base_type: string;
  description?: string;
}

export interface ChannelPayoutRate {
  id: string;
  channel_type: string;
  insurer: string | null;
  insurer_name: string | null;
  insurance_type: string | null;
  insurance_type_name: string | null;
  commission_percentage: string;
  premium_base: string;
  effective_from: string | null;
  effective_until: string | null;
  is_active: boolean;
}

export interface ChannelPayoutOverride {
  id: string;
  channel: string;
  insurer: string | null;
  insurer_name: string | null;
  insurance_type: string | null;
  insurance_type_name: string | null;
  commission_percentage: string;
  premium_base: string;
  effective_from: string | null;
  effective_until: string | null;
  is_active: boolean;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const channelsService = {
  async getUnifiedChannels(
    page = 1,
    params: { search?: string; page_size?: number; id?: string; type?: string } = {},
  ): Promise<PaginatedResponse<UnifiedChannelRow>> {
    const p = new URLSearchParams({ page: String(page) });
    if (params.search) p.append('search', params.search);
    if (params.page_size) p.append('page_size', String(params.page_size));
    // `type` is a raw Channel.TYPE_CHOICES value, matched against `type_label`
    // SERVER-SIDE and BEFORE pagination (`unified`, views.py), so `count` is
    // the filtered count. The channels list used to filter by type in the
    // browser over the twenty rows it happened to be holding: the header kept
    // saying "37 channels & POSPs", the pager still offered every page, and an
    // agent on page 2 was simply absent from a list that claimed to be
    // "POSP" -- including for the /posps -> ?type=POSP redirect.
    // `id` narrows the merged list to one row SERVER-SIDE, before pagination.
    // The detail page used to load page 1 and scan it, so a broker with more
    // than 20 channels or agents could not open the ones past that page --
    // they rendered "Channel not found" while every call returned 200.
    if (params.id) p.append('id', params.id);
    if (params.type) p.append('type', params.type);
    const res = await api.get<PaginatedResponse<UnifiedChannelRow>>(
      `/brokers/broker-channels/unified/?${p}`,
    );
    return res.data;
  },

  // ── Channel types ──
  // `params` exists for the Add-Channel dropdown, which must load EVERY active
  // type in one go: the default page size is 20, so a broker with 21 types
  // silently lost the rest from the picker.
  async getChannelTypes(
    page = 1,
    params: { is_active?: boolean; page_size?: number } = {},
  ): Promise<PaginatedResponse<ChannelType>> {
    const p = new URLSearchParams({ page: String(page) });
    if (params.is_active !== undefined) p.append('is_active', String(params.is_active));
    if (params.page_size !== undefined) p.append('page_size', String(params.page_size));
    const res = await api.get<PaginatedResponse<ChannelType>>(`/brokers/channel-types/?${p}`);
    return res.data;
  },
  /** Every channel this broker owns, under the type it belongs to.
   *
   *  Grouped SERVER-SIDE over the whole book. Paging the channel list and
   *  grouping what came back is the "first page only" bug the channel detail
   *  page had: a broker with more channels than one page silently loses the
   *  rest, and here that would read as "this type has no channels". */
  async getChannelTypeGroups(): Promise<ChannelTypeGroups> {
    const res = await api.get<ChannelTypeGroups>('/brokers/channel-types/grouped/');
    return res.data;
  },

  async createChannelType(data: CreateChannelTypePayload): Promise<ChannelType> {
    const res = await api.post<ChannelType>('/brokers/channel-types/', data);
    return res.data;
  },
  async deleteChannelType(id: string): Promise<void> {
    await api.delete(`/brokers/channel-types/${id}/`);
  },

  // ── Add a concrete channel (e.g. a POSP named Raman) ──
  async createChannel(data: CreateChannelPayload): Promise<{ id: string }> {
    const res = await api.post<{ id: string }>('/brokers/broker-channels/', data);
    return res.data;
  },

  // ── Edit an existing channel ──
  // Pass the CHANNEL id — use channelIdOf(row), never row.id (see the id trap).
  // On a POSP-backed channel the server also mirrors name/email/phone/commission
  // onto the POSP's user and its catch-all payout rate.
  async updateChannel(channelId: string, data: UpdateChannelPayload): Promise<{ id: string }> {
    const res = await api.patch<{ id: string }>(`/brokers/broker-channels/${channelId}/`, data);
    return res.data;
  },

  // ── Payout grid per channel type ──
  async getChannelPayoutRates(channelTypeId: string): Promise<PaginatedResponse<ChannelPayoutRate>> {
    const res = await api.get<PaginatedResponse<ChannelPayoutRate>>(
      `/brokers/channel-payout-rates/?channel_type=${channelTypeId}`,
    );
    return res.data;
  },
  async createChannelPayoutRate(data: Partial<ChannelPayoutRate>): Promise<ChannelPayoutRate> {
    const res = await api.post<ChannelPayoutRate>('/brokers/channel-payout-rates/', data);
    return res.data;
  },
  async deleteChannelPayoutRate(id: string): Promise<void> {
    await api.delete(`/brokers/channel-payout-rates/${id}/`);
  },

  // ── Per-channel payout override ──
  async getChannelPayoutOverrides(channelId: string): Promise<PaginatedResponse<ChannelPayoutOverride>> {
    const res = await api.get<PaginatedResponse<ChannelPayoutOverride>>(
      `/brokers/channel-payout-overrides/?channel=${channelId}`,
    );
    return res.data;
  },
  async createChannelPayoutOverride(data: Partial<ChannelPayoutOverride>): Promise<ChannelPayoutOverride> {
    const res = await api.post<ChannelPayoutOverride>('/brokers/channel-payout-overrides/', data);
    return res.data;
  },
  async deleteChannelPayoutOverride(id: string): Promise<void> {
    await api.delete(`/brokers/channel-payout-overrides/${id}/`);
  },
};

// ─── Override rights (#339): read-only channel workspace ("preview as partner") ──

export interface ChannelWorkspace {
  read_only: true;
  banner: string;
  channel: {
    id: string;
    name: string;
    code: string;
    channel_type: string;
    is_active: boolean;
    email: string;
    phone: string;
    parent: string;
    posp_id: string | null;
  };
  /** true when the viewer lacks policies.read: policies omitted (#339). */
  policies_hidden: boolean;
  policy_summary: { count: number; total_premium: string } | null;
  policies: Array<{
    id: string;
    policy_number: string;
    holder_name: string;
    insurer_name: string;
    total_premium: string;
    status: string;
    inception_date: string;
    expiry_date: string;
  }>;
}

export async function getChannelWorkspace(channelId: string): Promise<ChannelWorkspace> {
  const { data } = await api.get<ChannelWorkspace>(`/brokers/broker-channels/${channelId}/workspace/`);
  return data;
}
